import { z } from "zod";
import { CrewAvailabilityById } from "../types/CrewAvailability";
import {
  DayKey,
  addDaysToDayKey,
  addMonthsToDayKey,
  londonDayAtTime,
  londonDayKey,
  londonEndOfDay,
  londonStartOfDay,
  londonTimeAsLocalDate,
} from "@/lib/availability-dates";

export const addAvailabilityFormSchema = z.object({
  start_date: z.string({
    required_error: "Start date is required",
  }),
  end_date: z.string({
    required_error: "End date is required",
  }),
  start_time: z.date().optional(),
  end_time: z.date().optional(),
  all_day: z.boolean().nullish().default(false),
  recurrence_rule: z.string().optional(),
  recurrence_parent: z.number().optional(),
  end_repeat_rule: z.string().optional(),
  end_repeat_frequency: z.string().optional().refine((val) => {
    if (!val) return true; // optional, so empty is ok
    return !isNaN(Number(val)) && Number(val) > 0;
  }, { message: "Must be a number" }),
  end_repeat: z.string().optional().refine((val) => {
    if (!val) return true; // optional, so empty is ok
    return !isNaN(Number(val)) && Number(val) > 0;
  }, { message: "Must be a number" }),
  type: z.string().optional(),
  is_recurrence: z.boolean().optional(),
}).superRefine((data, ctx) => {
  // If recurrence_rule is set to anything other than "Never", require end_repeat and end_repeat_frequency
  if (data.recurrence_rule && data.recurrence_rule !== 'Never') {
    if (!data.end_repeat) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End repeat count is required when recurrence is set",
        path: ['end_repeat']
      });
    }

    if (!data.end_repeat_frequency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Repeat frequency is required when recurrence is set",
        path: ['end_repeat_frequency']
      });
    }
  }
});

export type AvailabilityFormValues = z.infer<typeof addAvailabilityFormSchema>;

/**
 * The time spinner hands back a Date in the device's zone. What the crew member actually chose is
 * the number they read off the wheel, so take the wall clock off it and apply it as London time —
 * the zone the office, the jobs and the rest of the system all work in. Previously this was
 * `new Date('yyyy-MM-dd')` (UTC midnight) mutated with `.setHours()` (device local), which stored
 * a different instant on a phone set to Warsaw than on one set to London.
 */
const wallClock = (time: Date | undefined): { hours: number; minutes: number } | null =>
  time ? { hours: time.getHours(), minutes: time.getMinutes() } : null;

export type AvailabilityRange = { from_date: Date; to_date: Date };

export const resolveAvailabilityRange = (
  data: Pick<AvailabilityFormValues, 'all_day' | 'start_time' | 'end_time'>,
  startDay: DayKey,
  endDay: DayKey,
): AvailabilityRange => {
  // An all-day period has to reach the end of the last day. It used to be stored as
  // midnight-to-midnight UTC, which in British Summer Time meant 01:00 to 01:00 — a single
  // all-day day came out zero length, and a multi-day one lost its final day entirely.
  if (data.all_day) {
    return { from_date: londonStartOfDay(startDay), to_date: londonEndOfDay(endDay) };
  }

  const start = wallClock(data.start_time);
  const end = wallClock(data.end_time);

  return {
    from_date: start ? londonDayAtTime(startDay, start.hours, start.minutes) : londonStartOfDay(startDay),
    to_date: end ? londonDayAtTime(endDay, end.hours, end.minutes) : londonEndOfDay(endDay),
  };
};

/** Returns a message to show the crew member, or null when the range is usable. */
export const validateAvailabilityRange = ({ from_date, to_date }: AvailabilityRange): string | null => {
  if (to_date.getTime() <= from_date.getTime()) {
    return "The end of the period has to come after the start. Check the times you picked.";
  }
  return null;
};

type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY';

const shiftDayKey = (day: DayKey, freq: RecurrenceFreq, step: number): DayKey => {
  switch (freq) {
    case 'DAILY':
      return addDaysToDayKey(day, step);
    case 'WEEKLY':
      return addDaysToDayKey(day, step * 7);
    case 'MONTHLY':
      return addMonthsToDayKey(day, step);
  }
};

/**
 * The occurrences *after* the first one.
 *
 * The caller writes the first occurrence as the parent row, so including it here created every
 * recurring period twice. The office page works around the same thing with an `events.shift()`;
 * this starts at the second occurrence instead.
 *
 * Shifting the day keys rather than mutating Dates with setDate/setMonth also keeps the wall-clock
 * time intact across a clock change — a weekly 09:00 booking stays at 09:00 through October.
 */
const generateRecurrenceDates = (
  data: Pick<AvailabilityFormValues, 'all_day' | 'start_time' | 'end_time'>,
  startDay: DayKey,
  endDay: DayKey,
  recurrenceRule: string | null,
  endRepeatCount: number | null,
  endRepeatFrequency: number | null
): AvailabilityRange[] => {
  if (!recurrenceRule || recurrenceRule === "Never") {
    return [];
  }

  const freq = recurrenceRule.split(";")[0]?.split("=")[1] as RecurrenceFreq | undefined;
  if (freq !== 'DAILY' && freq !== 'WEEKLY' && freq !== 'MONTHLY') {
    return [];
  }

  const interval = endRepeatFrequency || 1;
  const count = endRepeatCount || 10;
  const recurrenceDates: AvailabilityRange[] = [];

  for (let i = 1; i < count; i++) {
    recurrenceDates.push(
      resolveAvailabilityRange(
        data,
        shiftDayKey(startDay, freq, i * interval),
        shiftDayKey(endDay, freq, i * interval),
      )
    );
  }

  return recurrenceDates;
};

export const mapAvailabilityFormForFrontend = (
  availability: CrewAvailabilityById
): AvailabilityFormValues => {

  const recurrenceRule = availability.recurrence_rule
    ? availability.recurrence_rule.split(';').find(part => part.startsWith('FREQ='))?.split('=')[1] || 'Never'
    : undefined;

  const formattedRecurrenceRule = recurrenceRule
    ? recurrenceRule.charAt(0).toUpperCase() + recurrenceRule.slice(1).toLowerCase()
    : 'Never';

  // COUNT is how many occurrences, INTERVAL is the gap between them. These two were read into each
  // other's field, so opening a repeating period for edit swapped them and saving it back wrote
  // the swap to the database.
  const endRepeat = availability.recurrence_rule
    ? availability.recurrence_rule.split(';').find(part => part.startsWith('COUNT='))?.split('=')[1]
    : undefined;

  const endRepeatFrequency = availability.recurrence_rule
    ? availability.recurrence_rule.split(';').find(part => part.startsWith('INTERVAL='))?.split('=')[1]
    : undefined;

  return {
    // London days, not UTC days: a period running to 00:30 London is still that day's booking.
    start_date: availability.from_date ? londonDayKey(availability.from_date) : '',
    end_date: availability.to_date ? londonDayKey(availability.to_date) : '',
    // The picker shows a Date's device-local wall clock, so seed it with one carrying the London
    // time this period was stored at — otherwise editing from an away-from-home phone silently
    // shifted the booking.
    start_time: availability.from_date ? londonTimeAsLocalDate(availability.from_date) : undefined,
    end_time: availability.to_date ? londonTimeAsLocalDate(availability.to_date) : undefined,
    all_day: availability.all_day || false,
    recurrence_rule: formattedRecurrenceRule,
    is_recurrence: availability.is_recurrence,
    end_repeat_frequency: endRepeatFrequency,
    end_repeat: endRepeat,
    type: availability.type?.toString() || '0',
  };
};

export const mapCreateCrewAvailabilityForBackend = (
  data: AvailabilityFormValues,
  isEmployee: boolean,
  range: AvailabilityRange,
  crewId: number | null,
  isAvailability: boolean
) => {
  if (!crewId) return;

  const reoccurenceRule = data.recurrence_rule && data.recurrence_rule !== 'Never' ?
    `FREQ=${data.recurrence_rule.toUpperCase()};INTERVAL=${data.end_repeat_frequency};COUNT=${data.end_repeat}`
    : null

  const recurrenceDates = generateRecurrenceDates(
    data,
    data.start_date,
    data.end_date,
    reoccurenceRule,
    data.end_repeat ? parseInt(data.end_repeat) : null,
    data.end_repeat_frequency ? parseInt(data.end_repeat_frequency) : null
  );

  return {
    crew_id: Number(crewId),
    available: isAvailability,
    type: data.type !== undefined ? parseInt(data.type) : null,
    all_day: data.all_day,
    from_date: range.from_date,
    to_date: range.to_date,
    recurrence_rule: reoccurenceRule,
    recurrence_dates: recurrenceDates,
    approved: isEmployee ? 0 : 1
  };
};

export const getRepeatFrequencyText = (recurrence_rule: string | undefined) => {
  switch (recurrence_rule) {
    case 'Daily':
      return 'day(s)';
    case 'Weekly':
      return 'week(s)';
    case 'Monthly':
      return 'month(s)';
    default:
      return '';
  }
}
